import type { MouseEventHandler } from 'react';
import { useEffect } from 'react';
import { connect } from 'react-redux';

import { cn } from 'helpers/classnames';
import SearchInlineRecos from 'components/search/SearchInlineRecos';
import SponsoredAdsWrapper from 'components/search/SponsoredAdsWrapper';
import { evRecommendationClick } from 'events/recommendations';
import useMartyContext from 'hooks/useMartyContext';
import AdsRecosRow from 'components/search/AdsRecosRow';
import { track } from 'apis/amethyst';
import { trackEvent } from 'helpers/analytics';
import type { Product } from 'constants/searchTypes';
import ProductSearch from 'components/landing/ProductSearch';
import type { SponsoredAdProduct } from 'apis/sponsoredAds';
import { getProductRelations } from 'actions/products';
import { setUrlUpdated } from 'actions/search';
import { trackSponsoredAdsEvents } from 'hooks/useSponsoredAds';
import { AMPLITUDE_MODULE_CLICKED, AMPLITUDE_PRODUCT_CLICKED_EVENT_ID, GTM_EVENT_KEY } from 'constants/amplitude';
import { fireAmplitudeModuleClickedEvent } from 'utils/amplitude';
import type { AppState } from 'types/app';
import ProductUtils, { getProductGender, priceToFloat } from 'helpers/ProductUtils';

import css from 'styles/components/search/inlineAdSlot.scss';

interface PortalProps {
  WrapperEl?: string;
  titleClassName?: any;
  className: string;
  rowIndex?: number;
  attributes: { [key: string]: any };
  children?: Element;
}

interface Props {
  sponsoredAds: SponsoredAdProduct[];
  [key: string]: any;
}

export const InlineAdSlot = ({
  makeCardClick,
  heartsInfo,
  inlineRecos,
  filters: { sort, page, term, originalTerm },
  crossSiteRecos,
  isLoadingSymphony,
  sponsoredAds,
  getProductRelations,
  productCardsCount,
  rowIndex,
  sessionId
}: Props) => {
  const {
    marketplace: {
      search: { msaMelodyImageParams, hasSponsoredAds, searchInlineRecosRowIndex }
    }
  } = useMartyContext();

  useEffect(() => {
    if (inlineRecos) {
      const styleIds = inlineRecos.recos.map((reco: { styleId: string }) => reco.styleId);
      getProductRelations(styleIds);
    }
  }, [inlineRecos]);

  const hasDefaultSort = !sort || sort?.bestForYou || sort?.relevance || !Object.keys(sort).length;
  const hasMarketplaceRecos = crossSiteRecos?.products?.length > 3 && !page && hasDefaultSort;
  const hasInlineRecos = originalTerm && inlineRecos?.recos?.length >= 2;
  const numSponsoredAds = sponsoredAds?.length;
  const sponsoredAdsToShow = !!numSponsoredAds && rowIndex && hasSponsoredAds;

  const makeSponsoredAdClick = (product: Product, index: number, amazonAdsEventUrl: string, relatedStyles?: Product[]) => {
    const onCardClick = makeCardClick(product, index);
    const { productId, styleId, colorId } = product;
    return (e: MouseEventHandler) => {
      track(() => [
        evRecommendationClick,
        {
          index,
          recommendationType: 'PRODUCT_RECOMMENDATION',
          productIdentifiers: {
            productId,
            styleId,
            colorId
          },
          // NOTE: MICROSOFT values are required by Amethyst proto definition
          // See: https://code.amazon.com/packages/AmethystEvents/blobs/mainline/--/configuration/include/com/zappos/amethyst/website/WebsiteEnums.proto
          recommendationSource: 'MICROSOFT',
          widgetType: 'MICROSOFT_TOP_BLOCK',
          sourcePage: 'SEARCH_PAGE'
        }
      ]);
      trackEvent('TE_SEARCH_INLINE_AD_RECOMMENDATION_CLICK', `styleId:${styleId}:productIndex:${index}`);

      if (amazonAdsEventUrl) {
        trackSponsoredAdsEvents({ url: amazonAdsEventUrl, fetcherName: 'SponsoredAdsClickTracker' });
      }

      const selectedStyle = relatedStyles?.find(style => style.styleId === styleId);
      const isOnSale = selectedStyle ? ProductUtils.isStyleOnSale(selectedStyle) : false;
      const gender = getProductGender(product.txAttrFacet_Gender) ?? '';

      const moduleProduct = {
        brand: product.brandName,
        category: product.productType ?? '',
        gender,
        list_price: priceToFloat(product.originalPrice),
        name: product.productName,
        on_sale: isOnSale,
        price: product.price,
        sku: productId,
        styleId,
        subCategory: '',
        subcategory2: '',
        position: index + 1
      };

      fireAmplitudeModuleClickedEvent({
        event: AMPLITUDE_MODULE_CLICKED,
        sessionId,
        module: {
          type: 'product',
          name: 'sponsored'
        },
        product: moduleProduct,
        [GTM_EVENT_KEY]: AMPLITUDE_PRODUCT_CLICKED_EVENT_ID
      });

      onCardClick(e);
    };
  };

  // default is P13N product ads
  let portalProps: PortalProps = { className: '', attributes: {} };
  let portalContent;

  if (isLoadingSymphony) {
    return null;
  }

  if (hasInlineRecos) {
    portalProps = {
      WrapperEl: 'aside',
      className: css.inlineAdWrapper,
      rowIndex: searchInlineRecosRowIndex,
      attributes: {
        'data-reco-count': inlineRecos.recos.length,
        'data-test-id': 'searchResultsRecos'
      }
    };

    portalContent = (
      <SearchInlineRecos
        sessionId={sessionId}
        titleClassName={css.inlineTitle}
        heartsInfo={heartsInfo}
        inlineRecos={inlineRecos}
        msaImageParams={msaMelodyImageParams}
        searchTerm={term}
      />
    );
  }

  // new implementation of sponsored ads endpoint.
  if (numSponsoredAds > 0) {
    // we need a better way to handle this.
    // const hasSecondRowAds = true || hasInlineRecos || sponsoredAdsToShow || hasMarketplaceRecos;

    //if second row ads are showing, we need to remove one from the index to get correct the desired row.
    portalProps = {
      className: cn(css.sponsoredAdsWrapper, `sponsoredAds-${rowIndex}`),
      rowIndex,
      attributes: { 'data-test-id': `sponsoredResultsAds-${rowIndex}` }
    };

    portalContent = (
      <SponsoredAdsWrapper
        numSponsoredAds={numSponsoredAds}
        sponsoredAdsResults={sponsoredAds}
        msaImageParams={msaMelodyImageParams}
        makeSponsoredAdClick={makeSponsoredAdClick}
        sponsoredAdsToShow={sponsoredAdsToShow}
      />
    );
  }

  // Marketplace Recos
  if (hasMarketplaceRecos) {
    portalProps = {
      className: css.marketplaceRecosWrapper,
      rowIndex: searchInlineRecosRowIndex,
      attributes: { 'data-test-id': 'marketplaceRecos' }
    };

    portalContent = (
      <ProductSearch
        slotIndex={0}
        slotName={'marketplaceRecos'}
        slotHeartsData={{}}
        onComponentClick={() => {}}
        shouldLazyLoad={false}
        isFullWidth={false}
        slotDetails={crossSiteRecos}
        isOnSearchPage
      />
    );
  }

  return (
    <AdsRecosRow {...portalProps} cardCount={productCardsCount} debounceWait={100}>
      {portalContent}
    </AdsRecosRow>
  );
};

interface InlineSponsoredAdsProps extends Props {
  term?: string;
}

export const InlineSponsoredAds = (props: InlineSponsoredAdsProps) => {
  const bottomSlotDisplayFourAds = props.sponsoredAds?.slice(0, 4);

  return <ConnectedInlineAdSlot {...props} sponsoredAds={bottomSlotDisplayFourAds} forceNoMsftAds={true} />;
};

const mapStateToProps = (state: AppState) => {
  const { cookies } = state;
  const sessionId = cookies['session-id'] ?? '';
  return {
    sessionId
  };
};

const ConnectedInlineAdSlot = connect(mapStateToProps, { getProductRelations, setUrlUpdated })(InlineAdSlot);
export default ConnectedInlineAdSlot;
